/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'src/@vymo/ui/atoms';
import { Card, TabLayout, Table } from 'src/@vymo/ui/blocks';
import SkeletonLoader from 'src/@vymo/ui/blocks/skelton';
import { Keys, locale } from 'src/i18n';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import EmptyState from '../components/emptyState';
import ErrorState from '../components/errorState';
import { keyMetricDetailsNavigation } from '../queries';
import {
  getKeyMetricsGroups,
  getKeyMetricsList,
  getKeyMetricsPaginationInfo,
  getStatus,
} from '../selector';
import { fetchKeyMetrics } from '../thunk';
import { keyMetricsColumns } from './constants';
import styles from './index.module.scss';

function KeyMetrics() {
  const dispatch = useAppDispatch();
  const keyMetrics = useAppSelector(getKeyMetricsList);
  const keyMetricsGroups = useAppSelector(getKeyMetricsGroups);
  const paginationInfo = useAppSelector(getKeyMetricsPaginationInfo);
  const status = useAppSelector((state) => getStatus(state, 'key_metrics'));
  const [currentSelectedPage, setCurrentPage] = useState<number>(1);
  const [selectedGroup, setSelectedGroup] = useState<{
    id: string;
    name: string;
    goal_definition_code: string[];
  }>(keyMetricsGroups?.[0]);

  useEffect(() => {
    setSelectedGroup(keyMetricsGroups?.[0]);
  }, [keyMetricsGroups]);

  useEffect(() => {
    const skip = (currentSelectedPage - 1) * 10;
    dispatch(fetchKeyMetrics({ limit: 10, skip }));
  }, [currentSelectedPage, dispatch]);

  const filteredKeyMetrics = useMemo(() => {
    if (selectedGroup?.id) {
      return keyMetrics.filter((metric) =>
        (selectedGroup.goal_definition_code || [])?.includes(metric.code),
      );
    }
    return keyMetrics;
  }, [keyMetrics, selectedGroup?.goal_definition_code, selectedGroup?.id]);

  const paginationButtons = () => (
    <div className={styles.wrapper__paginationBtns}>
      {currentSelectedPage > 1 && (
        <Button
          size="small"
          type="outlined"
          rounded
          onClick={() => setCurrentPage((prev) => prev - 1)}
        >
          {locale(Keys.VIEW_PREVIOUS_METRICS)}
        </Button>
      )}
      {Number(paginationInfo?.limit) > 0 && (
        <Button
          size="small"
          type="outlined"
          rounded
          onClick={() => setCurrentPage((prev) => prev + 1)}
        >
          {locale(Keys.VIEW_NEXT_METRICS)}{' '}
        </Button>
      )}
    </div>
  );

  const tableView = () => (
    <Table
      tableData={filteredKeyMetrics}
      columnConfigs={keyMetricsColumns}
      isAccordion={false}
      tableConfig={{
        name: '',
        hideColumnConfigurator: true,
        onRowClick: (_, row) =>
          keyMetricDetailsNavigation({
            params: {
              frequency: row?.card?.frequency ?? '',
              start_date: row?.card?.date_range?.start ?? '',
              title: row?.card?.title,
            },
            code: row?.code,
            dispatch,
          }),
      }}
    />
  );

  return (
    <Card classNames={styles.wrapper}>
      {Boolean(status === 'loading' || !status) && (
        <SkeletonLoader height={300} rect />
      )}
      {Boolean(status === 'done' && keyMetricsGroups?.length > 0) && (
        <>
          <TabLayout
            items={keyMetricsGroups?.map((group) => ({
              ...group,
              label: group.name,
              key: group.id,
              children: tableView(),
            }))}
            defaultKey={selectedGroup?.id}
            onChange={(key, item) => setSelectedGroup(item)}
          />
          {Boolean(paginationInfo.limit || currentSelectedPage > 1) &&
            paginationButtons()}
        </>
      )}
      {Boolean(
        status === 'done' &&
          filteredKeyMetrics?.length > 0 &&
          keyMetricsGroups?.length === 0,
      ) && (
        <>
          {tableView()}
          {Boolean(paginationInfo.limit || currentSelectedPage > 1) &&
            paginationButtons()}
        </>
      )}
      {Boolean(status === 'done' && !keyMetrics?.length) && (
        <EmptyState
          message={locale(Keys.NO_DATA_TO_SHOW, {
            tab: 'key metrics',
          })}
        />
      )}
      {status === 'error' && <ErrorState />}
    </Card>
  );
}

export default KeyMetrics;
